/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { BuilderNavigationService, BuilderUserContext, CommonActions, FormActionPipelines } from '@gh/builder-shared';
import { AelEvents, EMPTY_XERO_SYNC_PROGRESS, XeroSyncType } from '@gh/builder-shared-data';
import {
  getRouteInitData,
  getRouteQueryParam,
  pollProgress,
  ProgressStatusEventType,
  resetPrivilegedTokens,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { openInNewWindow, openInThisWindow } from '@gh/core-util';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { assign, every } from 'lodash';
import { Subscription } from 'rxjs';
import {
  isXeroSyncInProgress,
  SystemVarsAccountsXeroSync,
  SystemVarsAccountsXeroUrl,
} from '../../../resources/model/system-vars-accounts.model';
import { SystemVarsAccountsTabBusinessService } from '../../../resources/system-vars-accounts-tab-business.service';
import { SystemVarsAccountsXeroFormState } from './system-vars-accounts-xero-form.service';

@Component({
  selector: 'gh-system-vars-accounts-xero-subtab',
  template: require('./system-vars-accounts-xero-subtab.component.html'),
  styles: [require('./system-vars-accounts-xero-subtab.component.scss')],
  viewProviders: [
    SystemVarsAccountsXeroFormState,
  ],
})
export class SystemVarsAccountsXeroTabComponent implements OnInit, OnDestroy {
  @ViewChild('xeroWebhookUrl') xeroWebhookUrlEl: ElementRef;
  @ViewChild('xeroRedirectUri', { static: true }) xeroRedirectUriEl: ElementRef;
  isXeroMigrated: boolean = false;
  isXeroConnected: boolean = false;
  isModifyMode: boolean = false;
  saveWebhooKeyDisabled: boolean = false;
  originalXero: any;
  xeroSyncOptions: SystemVarsAccountsXeroSync = {
    syncCostCenters: false,
    syncJobNumber: false,
    syncContacts: false,
    syncInvoices: false,
  };

  private progressSubscription: Subscription;

  constructor(
    public form: SystemVarsAccountsXeroFormState,
    private route: ActivatedRoute,
    private router: Router,
    private systemVarsBusiness: SystemVarsAccountsTabBusinessService,
    private navigationService: BuilderNavigationService,
    private notifications: NotificationService,
    private dialogs: DialogService,
    private pipelines: FormActionPipelines,
    private actions: CommonActions,
    private userContext: BuilderUserContext,
    private waitingService: WaitingService,
  ) {
  }

  ngOnInit(): void {
    const { originalXero } = getRouteInitData(this.route);
    const code = getRouteQueryParam(this.route, 'code');
    if (code) {
      this.systemVarsBusiness.connectXero(code).pipe(
        this.waitingService.operator(),
        switchMap$((data) =>  [data]))
        .subscribe(
          (data) => {
            if (data) {
              this.isXeroConnected = true;
              this.originalXero = data;
              this.form.build(data);
              this.form.setFieldsPrivilegedEnabled(false);
            } else {
              this.form.clearConnectionFields();
            }
            this.removeParamFromUrl();
          },
          this.notifications.processAll);
    }
    assign(this, { originalXero });
    this.form.build(originalXero);
    this.setIsConnected();
    this.updateSaveWebhookDisabled();

    if (this.form.isAnySyncInProgress) {
      this.notifications.warning('msg_sync_in_progress');
    }

    this.progressSubscription = pollProgress({
      empty: EMPTY_XERO_SYNC_PROGRESS,
      isInProgress: isXeroSyncInProgress,
      status: this.form.progress$,
      poll: () => this.systemVarsBusiness.getXeroSyncProgress(),
    }).subscribe(({ type, nextStatus }) => {
      if (type !== ProgressStatusEventType.Start || this.form.isInitialSyncSuccessed) {
        this.form.setSyncProgress(nextStatus);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  get isUserSwitched(): boolean {
    return this.userContext.isSwitched();
  }

  get isModifyDisabled(): boolean {
    return !this.isXeroConnected || this.isModifyMode;
  }

  get isEditOrModifyMode(): boolean {
    return this.form.editable || this.isModifyMode;
  }

  get isWebhookKeyActive(): boolean {
    return Boolean(this.form.getControl('xeroWebhookKey').value);
  }

  get isWebhookUrlActive(): boolean {
    return Boolean(this.form.getControl('xeroWebhookUrl').value);
  }

  get isRedirectUriActive(): boolean {
    return Boolean(this.form.getControl('xeroCallbackUrl').value);
  }

  get isFormFieldHidden(): boolean {
    return this.isXeroConnected && !this.isModifyMode;
  }

  get saveWebhookDisabled(): boolean {
    return !this.isEditOrModifyMode || !this.isWebhookUrlActive
      || !this.isWebhookKeyActive || this.saveWebhooKeyDisabled;
  }

  updateSaveWebhookDisabled(): void {
    const webhookKeyValue = this.form.getControl('xeroWebhookKey').value;
    this.saveWebhooKeyDisabled = webhookKeyValue === this.originalXero.xeroWebhookKey;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.IntegrationAccounts],
      label: label ? label : 'lbl_integration_accounts',
    });
  }

  setIsConnected(): void {
    this.isXeroConnected = every([
        this.originalXero.xeroClientId,
        this.originalXero.xeroClientSecret,
        this.originalXero.organizationName,
            ]);

    if (this.isXeroConnected) {
      this.form.setFieldsPrivilegedEnabled(false);
    }
  }

  setSyncOption(event: MatCheckboxChange): void {
    this.xeroSyncOptions[event.source.value] = event.checked;
  }

  connect(): void {
    this.systemVarsBusiness
      .generateXeroAcceptUrl(this.form.submitValue)
      .pipe(this.waitingService.operator())
      .subscribe(
        (xeroAcceptUrl: SystemVarsAccountsXeroUrl) =>
        openInThisWindow(xeroAcceptUrl.url),
        this.notifications.processAll);
  }

  modify(): void {
    this.systemVarsBusiness
      .modifyXero({...this.form.submitValue})
      .pipe(this.waitingService.operator())
      .subscribe(
        (xeroAcceptUrl: SystemVarsAccountsXeroUrl) =>
        openInThisWindow(xeroAcceptUrl.url),
        this.notifications.processAll);
  }

  generateWebhookUrl(): void {
    this.systemVarsBusiness
      .getXeroWebhookUrl().pipe(
      (this.waitingService.operator()))
      .subscribe(
        (data) => {
          this.originalXero = data;
          this.form.build(data);
          if (this.isXeroConnected) {
            this.form.setFieldsPrivilegedEnabled(false);
          }
        },
        this.notifications.processAll,
      );
  }

  copyUrl(type: string): void {
    this[type].nativeElement.select();
    document.execCommand('copy');
  }

  saveWebhookKey(): void {
    this.systemVarsBusiness
      .saveXeroWebhookKey({
                            webhookKey: this.form.getControl('xeroWebhookKey').value,
                            etag: this.form.getControl('etag').value,
      }).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (data) => {
          this.originalXero = data;
          this.form.build(data);

          if (this.isXeroConnected) {
            this.form.setFieldsPrivilegedEnabled(false);
          }

          this.saveWebhooKeyDisabled = true;
        },
        this.notifications.processAll,
      );
  }

  sync(): void {
    this.systemVarsBusiness
      .syncWithXero(this.xeroSyncOptions).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (sync) => {
          this.form.setSyncProgress(sync.progress);
          this.notifications.success('msg_synchronization_initiated_successfully');
        },
        this.notifications.processAll,
      );
  }

  resync(): void {
    this.systemVarsBusiness.restartXeroSync()
      .pipe(this.waitingService.operator())
      .subscribe(
        ({ progress }) => this.form.setSyncProgress(progress),
        this.notifications.processAll);
  }

  onPrivilegedTokenReceived(): void {
    this.isModifyMode = true;
    this.form.setFieldsPrivilegedEnabled(true);
  }

  removeParamFromUrl(): void {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        // tslint:disable-next-line:no-null-keyword
        queryParams: { code: null },
        queryParamsHandling: 'merge',
      });
  }
}
